import React, { useEffect, useRef, useState } from 'react';

interface VoicePinPlayerProps {
  audioUrl?: string;
  audioBlob?: Blob;       // capture-time preview
  duration?: number;      // seconds, optional — derived from audio element if missing
  transcript?: string;    // if provided, renders with time-based char highlighting
  compact?: boolean;      // tight layout for saved-recorder state
  onEnded?: () => void;
  autoPlay?: boolean;
}

export const VoicePinPlayer: React.FC<VoicePinPlayerProps> = ({
  audioUrl, audioBlob, duration, transcript, compact, onEnded, autoPlay,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const mountedRef = useRef(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(duration ?? 0);
  const [srcUrl, setSrcUrl] = useState<string | undefined>(audioUrl);

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Create an object URL for blob-only playback.
  useEffect(() => {
    if (audioBlob && !audioUrl) {
      const url = URL.createObjectURL(audioBlob);
      setSrcUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setSrcUrl(audioUrl);
  }, [audioBlob, audioUrl]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    setCurrentTime(el.currentTime);
    if (!duration && el.duration && Number.isFinite(el.duration)) {
      setTotalDuration(el.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    onEnded?.();
  };

  const handleLoadedMetadata = () => {
    const el = audioRef.current;
    if (!el) return;
    if (!duration && el.duration === Infinity) {
      const onSeeked = () => {
        el.removeEventListener('seeked', onSeeked);
        if (!mountedRef.current) return;
        el.currentTime = 0;
        if (Number.isFinite(el.duration)) setTotalDuration(el.duration);
      };
      el.addEventListener('seeked', onSeeked);
      el.currentTime = 1e9;
      return;
    }
    handleTimeUpdate();
  };

  useEffect(() => {
    if (autoPlay && srcUrl) {
      audioRef.current?.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [autoPlay, srcUrl]);

  const pct = totalDuration > 0 ? Math.min(1, currentTime / totalDuration) : 0;

  return (
    <div className={`voice-pin-player ${compact ? 'compact' : ''}`}>
      <audio
        ref={audioRef}
        src={srcUrl}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
      />
      <button
        className="voice-pin-player-btn"
        onClick={togglePlay}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>
      <div className="voice-pin-player-track">
        <ScrubberWaveform progress={pct} />
        <div className="voice-pin-player-time">
          {formatTime(currentTime)} / {formatTime(totalDuration)}
        </div>
      </div>
      {transcript && (
        <TranscriptHighlight text={transcript} progress={pct} />
      )}
    </div>
  );
};

const ScrubberWaveform: React.FC<{ progress: number }> = ({ progress }) => {
  const bars = 18;
  const fillCount = Math.round(bars * progress);
  return (
    <div className="voice-pin-scrubber">
      {Array.from({ length: bars }).map((_, i) => {
        const h = 5 + ((i * 7) % 11);
        const filled = i < fillCount;
        return (
          <span
            key={i}
            className="voice-pin-scrubber-bar"
            style={{ height: h, background: filled ? '#D4715A' : '#E5D5CC' }}
          />
        );
      })}
    </div>
  );
};

const TranscriptHighlight: React.FC<{ text: string; progress: number }> = ({ text, progress }) => {
  const cut = Math.floor(text.length * progress);
  const spoken = text.slice(0, cut);
  const rest = text.slice(cut);
  return (
    <div className="voice-pin-transcript">
      <span className="voice-pin-transcript-spoken">{spoken}</span>
      <span className="voice-pin-transcript-rest">{rest}</span>
    </div>
  );
};

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}
